import * as HC from '@radix-ui/react-hover-card';
import { ArrowUpRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Sparkline } from '@/components/charts/Sparkline';
import { Badge } from './Badge';
import { cn } from './cn';
import { EntityChipIcon, type EntityKind } from './entityIcons';

/*
  EntityPeek — живое имя (DESIGN.md R26).
  Приём характера: имя узла, адрес или номер машины остаются живыми везде, где встретились —
  в тексте проблемы, в письме, в логе. Имя помечено пунктиром снизу и иконкой своего типа,
  наведение раскрывает карточку с состоянием сейчас. Не нужно уходить со страницы, чтобы
  вспомнить, что такое cn338; ссылка на объект перестаёт быть простым текстом.

  Состояние карточки — один из тонов Badge; метрики показываются вместе со своим следом.
*/

export interface PeekMetric {
  label: string;
  value: string;
  trend?: number[];
}

interface EntityPeekProps {
  kind: EntityKind;
  name: string;
  /** «Гипервизор · AMS · Proxmox 8.2». */
  subtitle?: string;
  state?: { label: string; variant: 'success' | 'warn' | 'urgent' | 'neutral' | 'info' };
  metrics?: PeekMetric[];
  /** Строка под метриками: «2 проблемы за сутки». */
  note?: string;
  /** Чем открыть объект: своя кнопка или ссылка. */
  action?: ReactNode;
  /** Если нужно обернуть не имя, а произвольный элемент. */
  children?: ReactNode;
  className?: string;
}

export function EntityPeek({
  kind,
  name,
  subtitle,
  state,
  metrics,
  note,
  action,
  children,
  className,
}: EntityPeekProps) {
  return (
    <HC.Root openDelay={180} closeDelay={120}>
      <HC.Trigger asChild>
        {children ?? (
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-1 rounded-xs align-baseline font-medium text-fg',
              'underline decoration-line-strong decoration-dotted underline-offset-4',
              'transition-colors duration-fast hover:decoration-brand focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
              className,
            )}
          >
            <EntityChipIcon kind={kind} className="size-3.5 shrink-0 text-fg-subtle" />
            {name}
          </button>
        )}
      </HC.Trigger>
      <HC.Portal>
        <HC.Content
          side="top"
          align="start"
          sideOffset={8}
          data-slot="entity-peek"
          className="z-popover w-72 animate-pop-in rounded-lg border border-line bg-elevated p-3 shadow-lg"
        >
          <div className="flex items-start gap-2">
            <span className="grid size-7 shrink-0 place-items-center rounded-sm bg-muted text-fg-muted">
              <EntityChipIcon kind={kind} className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-fg">{name}</div>
              {subtitle && <div className="truncate text-2xs text-fg-subtle">{subtitle}</div>}
            </div>
            {state && (
              <Badge variant={state.variant} solid>
                {state.label}
              </Badge>
            )}
          </div>

          {metrics && metrics.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {metrics.map((m) => (
                <div key={m.label} className="min-w-0">
                  <div className="truncate text-2xs text-fg-subtle">{m.label}</div>
                  <div className="text-sm font-semibold text-fg tabular-nums">{m.value}</div>
                  {m.trend && <Sparkline values={m.trend} width={64} height={16} endDot={false} />}
                </div>
              ))}
            </div>
          )}

          {(note || action) && (
            <div className="mt-3 flex items-center justify-between gap-2 border-t border-line pt-2">
              {note && <span className="truncate text-2xs text-fg-subtle">{note}</span>}
              {action ?? (
                <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-brand-fg">
                  Открыть
                  <ArrowUpRight className="size-3.5" />
                </span>
              )}
            </div>
          )}
          <HC.Arrow className="fill-elevated" width={10} height={5} />
        </HC.Content>
      </HC.Portal>
    </HC.Root>
  );
}
